<template>
  <BaseListItem>
    <div class="flex text-sm">
      <div class="w-full">
        <div class="flex-1">
          <div class="flex py-3">
            <div class="w-64 px-3">
              <RouterLink
                :to="{
                  name: 'accounts.campaigns.show',
                  params: { accountId, campaignId },
                }"
              >
                <template v-if="cover">
                  <img
                    :key="`${_uid}-campaign-cover`"
                    :srcset="cover.srcsets || ''"
                    @load.once="onImageLoad"
                    sizes="1px"
                    :src="cover.url"
                    class="block rounded w-full"
                  />
                </template>
                <template v-else-if="project.cover">
                  <img
                    :key="`${_uid}-project-cover`"
                    sizes="1px"
                    :src="project.cover"
                    class="block rounded w-full"
                  />
                </template>
                <template v-else>
                  <div class="aspect-ratio-16/9 w-full rounded bg-gray-200" />
                </template>
              </RouterLink>
            </div>
            <div class="flex-1 px-3 space-y-4">
              <div class="justify-between flex items-start">
                <div>
                  <RouterLink
                    v-if="accounts.length < 2"
                    :to="{
                      name: 'accounts.campaigns.show',
                      params: { accountId, campaignId },
                    }"
                  >
                    {{ campaign.name }}
                  </RouterLink>
                  <template v-else>
                    <h3 class="text-base mb-2">
                      {{ campaign.name }}
                    </h3>
                  </template>

                  <div class="mt-2">
                    <RouterLink
                      v-if="accounts.length < 2"
                      :to="{
                        name: 'accounts.projects.project.campaigns',
                        params: {
                          accountId: campaign.accountId,
                          projectId: campaign.projectUuid,
                        },
                      }"
                      class="btn btn-brand capitalize text-sm mr-2"
                    >
                      open project
                    </RouterLink>
                    <template v-else>
                      <RouterLink
                        v-for="account in accounts"
                        :key="account.id"
                        :to="{
                          name: 'accounts.projects.project.campaigns',
                          params: {
                            accountId: account.id,
                            projectId: campaign.projectUuid,
                          },
                        }"
                        class="btn btn-brand capitalize text-sm mr-2"
                      >
                        {{ account.type }}
                      </RouterLink>
                    </template>
                    <Button
                      primary
                      class="btn btn-brand text-sm"
                      @click="$emit('edit', campaign.id)"
                    >
                      Quick Edit
                    </Button>
                  </div>
                </div>
                <div class="flex flex-col items-end space-y-2">
                  <BaseBadge green v-if="isActive">
                    ad plan status: <span class="uppercase">active</span>
                  </BaseBadge>
                  <BaseBadge silver>
                    Project status: <span class="uppercase">{{ project.status }}</span>
                  </BaseBadge>
                </div>
              </div>
              <p v-if="campaign.description" class="text-muted text-sm">
                {{ campaign.description }}
              </p>
            </div>
          </div>
          <div class="flex -mx-3 py-3">
            <div class="flex-none px-6">
              <span class="text-xs uppercase font-semibold text-gray-600">starts</span>
              <p v-if="campaign.dateStartForHumans" v-text="campaign.dateStartForHumans" />
              <p v-else>-</p>
            </div>
            <div class="flex-none px-6">
              <span class="text-xs uppercase font-semibold text-gray-600">ends</span>
              <p v-if="campaign.dateEndForHumans" v-text="campaign.dateEndForHumans" />
              <p v-else>-</p>
            </div>
            <div class="flex-none px-6">
              <span class="text-xs uppercase font-semibold text-gray-600">budget</span>
              <p v-if="campaign.budget">{{ campaign.budget }}&euro;</p>
              <p v-else>-</p>
            </div>
            <div class="flex-none px-6">
              <span class="text-xs uppercase font-semibold text-gray-600">target</span>
              <p>
                <span v-if="campaign.result" v-text="campaign.result" />
                {{ campaign.target ? campaign.target : '-' }}
              </p>
            </div>
            <div class="flex-none px-6 text-right">
              <span class="text-xs uppercase font-semibold text-gray-600"> budget / target</span>
              <p>{{ budgetPerResult ? budgetPerResult : '-' }}&euro;</p>
            </div>
            <div class="flex-none px-6 ml-auto">
              <div
                :class="[
                  'flex',
                  'bg-gray-900',
                  'px-6',
                  'pt-1',
                  'pb-3',
                  '-my-3',
                  'rounded',
                  'text-white',
                  'text-xl',
                  'font-semibold',
                ]"
              >
                <div class="flex-none text-left">
                  <span class="text-xs uppercase font-semibold text-gray-400"> CTR</span>
                  <p>{{ pageviewsPerImpression }}%</p>
                </div>

                <div class="flex-none text-left ml-6">
                  <span class="text-xs uppercase font-semibold text-gray-400"> CR</span>
                  <p>{{ conversionRate }}%</p>
                </div>

                <div class="flex-none text-left ml-6">
                  <span class="text-xs uppercase font-semibold text-gray-400"> leads</span>
                  <p>{{ campaign.totalContactRequests }}</p>
                </div>

                <div v-if="spendPerLead !== '-'" class="flex-none text-right ml-6">
                  <span class="text-xs uppercase font-semibold text-gray-400"> cost</span>
                  <p>{{ spendPerLead }} <span class="text-xs">&euro;</span></p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="campaign.totalChannels" class="pt-4">
            <table class="table w-full border-collapse">
              <thead>
                <tr class="bg-gray-100 text-sm text-gray-600 uppercase">
                  <th class="py-2 px-3 border border-l-0 text-left w-px" />
                  <th class="py-2 px-3 border text-left" colspan="2">channel</th>
                  <th class="py-2 px-3 border text-right">impressions</th>
                  <th class="py-2 px-3 border text-right">reach</th>
                  <th class="py-2 px-3 border text-right">frequency</th>
                  <th class="py-2 px-3 border text-right">pageviews</th>
                  <th class="py-2 px-3 border text-right">contact requests</th>
                  <th class="py-2 px-3 border border-r-0 text-right">spend</th>
                </tr>
              </thead>
              <FacebookTableRow
                v-if="facebookCampaigns && facebookCampaigns.length"
                :facebook-campaigns="facebookCampaigns"
                :facebook-campaign-totals="facebookCampaignTotals"
              />
              <GoogleAdsTableRow
                v-if="campaign.gaCampaigns && campaign.gaCampaigns.length"
                :ga-campaigns="campaign.gaCampaigns"
                :ga-campaign-reports="campaign.gaReportsAggregatedOnCampaignLevel"
              />
            </table>
          </div>
        </div>
      </div>
    </div>
  </BaseListItem>
</template>

<script>
import { flatMapDepth } from 'lodash-es';
import { mapState } from 'vuex';
import mediableMixin from '@/mixins/mediableMixin';
import { entityComputed } from '@/store/helper';
import FacebookTableRow from '@/components/Campaigns/FacebookTableRow.vue';
import GoogleAdsTableRow from '@/components/Campaigns/GoogleAdsTableRow.vue';

export default {
  name: 'ListItem',
  mixins: [mediableMixin],
  components: {
    FacebookTableRow,
    GoogleAdsTableRow,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showDetails: false,
  }),
  computed: {
    ...entityComputed,
    ...mapState('entities', {
      accounts(state) {
        const { accounts = [] } = this.campaign || {};

        if (!Array.isArray(accounts)) {
          return [];
        }

        return accounts.map((id) => state.accounts.byId[id]);
      },
    }),
    accountId() {
      return this.campaign.accountId;
    },
    campaignId() {
      return this.campaign.id;
    },
    cover() {
      if (!this.campaign || !this.campaign.media || !Array.isArray(this.campaign.media)) {
        return null;
      }

      const cover = this.campaign.media.find((medium) => medium.collection === 'covers');

      return cover || null;
    },
    project() {
      const { projectUuid } = this.campaign;
      if (!projectUuid) return null;
      return this.entityById('projects', projectUuid);
    },
    budgetPerResult() {
      if (!this.campaign) return null;
      if (!this.campaign.budget) return null;
      if (!this.campaign.result) return null;
      if (this.campaign.isBudgetPerResult) return this.campaign.budget;
      return (this.campaign.budget / this.campaign.result).toFixed(2);
    },
    toProject() {
      return {
        name: 'accounts.projects.project.campaigns',
        params: {
          accountId: this.accountId,
          projectId: this.campaign.projectUuid,
        },
      };
    },
    channels() {
      if (!this.campaign.channels) return [];
      return this.campaign.channels.map((channelId) => this.entityById('channels', channelId));
    },
    isActive() {
      return (this.campaign?.totalActiveChannels || 0) > 0;
    },
    facebookCampaigns() {
      return this.channels
        .filter((channel) => channel.channableType === 'facebook_campaign')
        .map((channel) => channel.channableData)
        .filter(Boolean);
    },
    facebookCampaignTotals() {
      return flatMapDepth(this.facebookCampaigns, 'facebook_campaign_insights', 1).reduce(
        (acc, current) => ({
          impressions: acc.impressions + (current?.impressions || 0),
          reach: acc.reach + (current?.reach || 0),
          clicks: acc.clicks + (current?.clicks || 0),
          inlineLinkClicks: acc.inlineLinkClicks + (current?.inline_link_clicks || 0),
          offsiteConversions: acc.offsiteConversions + (current?.offsiteConversions || 0),
          spend: acc.spend + (current?.spend || 0) / 100,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          inlineLinkClicks: 0,
          offsiteConversions: 0,
          spend: 0,
        }
      );
    },
    gaCampaignTotals() {
      return (this.campaign?.gaReportsAggregatedOnCampaignLevel || [])
        .filter((report) => report.ga_ad_group_id === 0 && report.ga_ad_id === 0)
        .reduce(
          (acc, current) => ({
            impressions: acc.impressions + (current?.impressions || 0),
            reach: acc.reach + (current?.reach || 0),
            clicks: acc.clicks + (current?.clicks || 0),
            inlineLinkClicks: acc.clicks + (current?.clicks || 0),
            spend: acc.spend + (current?.cost_micros || 0) / 1_000_000,
          }),
          {
            impressions: 0,
            reach: 0,
            clicks: 0,
            inlineLinkClicks: 0,
            offsiteConversions: 0,
            spend: 0,
          }
        );
    },
    campaignTotals() {
      return [this.facebookCampaignTotals, this.gaCampaignTotals].reduce(
        (acc, current) => ({
          impressions: acc.impressions + current?.impressions,
          reach: acc.reach + current.reach,
          clicks: acc.clicks + current?.clicks,
          inlineLinkClicks: acc.inlineLinkClicks + current.inlineLinkClicks,
          spend: acc.spend + current?.spend,
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          inlineLinkClicks: 0,
          offsiteConversions: 0,
          spend: 0,
        }
      );
    },
    pageviewsPerImpression() {
      const { impressions, inlineLinkClicks } = this.campaignTotals;
      if (!impressions) return 0;
      return +((inlineLinkClicks / impressions) * 100).toFixed(2);
    },
    conversionRate() {
      const { inlineLinkClicks } = this.campaignTotals;
      const { totalContactRequests: contactRequests = 0 } = this.campaign;

      if (!Number.isFinite(contactRequests) || !Number.isFinite(inlineLinkClicks)) return '-';

      if (inlineLinkClicks > 0) {
        return (100 * (contactRequests / inlineLinkClicks)).toFixed(2);
      }
      return '-';
    },
    spendPerLead() {
      const { spend = 0 } = this.campaignTotals || {};
      const { totalContactRequests: contactRequests = 0 } = this.campaign;
      if (Number.isFinite(spend) && Number.isFinite(contactRequests) && contactRequests > 0) {
        return (spend / contactRequests).toFixed(2);
      }
      return '-';
    },
  },
};
</script>
